import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { Building2, Loader2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin-only manager for which rep agencies carry a manufacturer.
 *
 * Reads /api/manufacturers/{id}/agencies for the current assignments and
 * presents:
 *   - A visible roster section (read-only for non-admins).
 *   - An "Edit assignments" dialog with a checkbox per agency.
 *
 * Saves via PUT /api/manufacturers/{id}/agencies which replaces the full set.
 */
export default function ManufacturerAgenciesSection({ manufacturerId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [assigned, setAssigned] = useState([]); // [{agency, line_id, active}]
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const reload = async () => {
    if (!manufacturerId) return;
    setLoading(true);
    try {
      const res = await api.get(`/manufacturers/${manufacturerId}/agencies`);
      setAssigned(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAssigned([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manufacturerId]);

  return (
    <div
      className="bg-white border border-border rounded-sm p-5"
      data-testid="manufacturer-agencies-section"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            <Building2 className="w-3.5 h-3.5" />
            Rep agencies
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {isAdmin
              ? "Assign this manufacturer to one or more rep agencies. Their distributors will see it in search."
              : "Rep agencies that carry this manufacturer."}
          </p>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditOpen(true)}
            className="rounded-sm shrink-0"
            data-testid="manage-agencies-btn"
          >
            <Pencil className="w-4 h-4 mr-1.5" />
            Edit assignments
          </Button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="assigned-agencies-list">
        {loading && assigned.length === 0 ? (
          <div className="text-xs text-muted-foreground font-mono-data">Loading…</div>
        ) : assigned.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            Not assigned to any agency yet.
          </div>
        ) : (
          assigned.map((a) => (
            <span
              key={a.line_id}
              className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-800 text-xs px-2.5 py-1 rounded-sm font-medium"
              data-testid={`assigned-agency-${a.agency.id}`}
            >
              {a.agency.name}
              {a.active === false && (
                <span className="text-[10px] uppercase tracking-wider text-amber-700">
                  inactive
                </span>
              )}
            </span>
          ))
        )}
      </div>

      {isAdmin && (
        <EditAssignmentsDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          manufacturerId={manufacturerId}
          initialAssignedIds={assigned.map((a) => a.agency.id)}
          onSaved={() => {
            setEditOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function EditAssignmentsDialog({
  open,
  onOpenChange,
  manufacturerId,
  initialAssignedIds,
  onSaved,
}) {
  const [allAgencies, setAllAgencies] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/rep-agencies")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setAllAgencies(
          list
            .filter((a) => a.active !== false)
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
      })
      .catch(() => setAllAgencies([]))
      .finally(() => setLoading(false));
    setSelected(new Set(initialAssignedIds));
  }, [open, initialAssignedIds]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put(`/manufacturers/${manufacturerId}/agencies`, {
        agency_ids: Array.from(selected),
      });
      toast.success("Agency assignments updated");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-agencies-dialog">
        <DialogHeader>
          <DialogTitle>Assign rep agencies</DialogTitle>
          <DialogDescription>
            Pick every rep agency that carries this manufacturer. Distributors
            of selected agencies will see it in their search.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto -mx-6 px-6 border-y border-border">
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              Loading agencies…
            </div>
          ) : allAgencies.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground italic">
              No rep agencies exist yet.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {allAgencies.map((a) => (
                <li
                  key={a.id}
                  className="py-3 flex items-center gap-3"
                  data-testid={`agency-row-${a.id}`}
                >
                  <Checkbox
                    id={`ag-${a.id}`}
                    checked={selected.has(a.id)}
                    onCheckedChange={() => toggle(a.id)}
                    data-testid={`agency-checkbox-${a.id}`}
                  />
                  <label
                    htmlFor={`ag-${a.id}`}
                    className="flex-1 cursor-pointer text-sm"
                  >
                    <div className="font-medium text-slate-900">{a.name}</div>
                    {a.website ? (
                      <div className="text-xs text-muted-foreground font-mono-data">
                        {a.website}
                      </div>
                    ) : null}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="rounded-sm"
            data-testid="edit-agencies-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || loading}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="edit-agencies-save"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save assignments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
